// Campo «Dirección»: país, departamento o estado, municipio o ciudad y la
// dirección escrita, como la ubicación del formulario de clientes
// (components/ui/location-field.tsx). Se guarda como JSON string
// `{"country":"HN","state":"FM","city":"Distrito Central","address":"Col. Palmira…"}`
// para caber en `customFieldValue`. Lógica pura: la usan el servidor
// (validación, búsqueda, PDF, reportes) y el navegador (formulario, tabla).
//
// - `country`: código ISO de `COUNTRIES` (se acepta también el nombre).
// - `state`: Honduras, código del departamento (`HONDURAS_DEPARTMENTS`);
//   Estados Unidos, código del estado (`US_STATES`); otros países, no lleva.
// - `city`: en Honduras, un municipio del departamento; en los demás, texto.
// - `address`: colonia, calle, casa, referencias.

import { COUNTRIES, normalizeCountryCode } from "../../lib/geo/countries";
import { HONDURAS_DEPARTMENTS, getDepartmentByCity } from "../../lib/geo/honduras";
import { US_STATES } from "../../lib/geo/us_states";

export interface AddressValue {
  country: string;
  state?: string;
  city?: string;
  address?: string;
}

const MAX_CITY = 120;
const MAX_ADDRESS = 500;

/** Otros nombres de un municipio de Honduras (la lista usa «Tegucigalpa», como Clientes). */
const MUNICIPIO_ALIASES: Record<string, string> = {
  "distrito central": "Tegucigalpa",
  "comayaguela": "Tegucigalpa",
  "comayagüela": "Tegucigalpa",
  "tegucigalpa m.d.c.": "Tegucigalpa",
};

const text = (v: unknown, max: number) => (typeof v === "string" ? v.trim().replace(/\s+/g, " ").slice(0, max) : "");

/** Código del departamento de Honduras por código o por nombre («Francisco Morazán»). */
function hondurasState(raw: string): string | undefined {
  if (!raw) return undefined;
  const lower = raw.toLowerCase();
  return HONDURAS_DEPARTMENTS.find((d) => d.value.toLowerCase() === lower || d.label.toLowerCase() === lower)?.value;
}

function usState(raw: string): string | undefined {
  if (!raw) return undefined;
  const lower = raw.toLowerCase();
  return US_STATES.find((s) => s.value.toLowerCase() === lower || s.label.toLowerCase() === lower)?.value;
}

/**
 * Lee un valor guardado (o recibido: objeto, JSON o texto suelto) como
 * dirección; null si no hay nada. Un texto que no es JSON es la dirección
 * escrita (país Honduras por defecto, como el formulario de clientes).
 * Lanza si el departamento o el municipio de Honduras no existen.
 */
export function parseAddress(raw: unknown): AddressValue | null {
  let value: unknown = raw;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (!t) return null;
    try {
      value = JSON.parse(t);
    } catch {
      value = { address: t };
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const obj = value as Record<string, unknown>;
  const country = normalizeCountryCode(text(obj.country, 60) || undefined);
  let city = text(obj.city ?? obj.municipality, MAX_CITY);
  const address = text(obj.address ?? obj.line ?? obj.street, MAX_ADDRESS);
  const stateRaw = text(obj.state ?? obj.department, 60);
  let state: string | undefined;
  if (country === "HN") {
    city = MUNICIPIO_ALIASES[city.toLowerCase()] ?? city;
    state = hondurasState(stateRaw) ?? (city ? getDepartmentByCity(city) : undefined);
    if (stateRaw && !hondurasState(stateRaw)) throw new Error(`«${stateRaw}» no es un departamento de Honduras`);
    if (city) {
      const dept = HONDURAS_DEPARTMENTS.find((d) => d.value === state);
      const muni = dept?.municipios.find((m) => m.value.toLowerCase() === city.toLowerCase());
      if (!muni) throw new Error(`«${city}» no es un municipio${dept ? ` de ${dept.label}` : " de Honduras"}`);
      return clean({ country, state, city: muni.value, address });
    }
  } else if (country === "US") {
    state = usState(stateRaw);
    if (stateRaw && !state) throw new Error(`«${stateRaw}» no es un estado de Estados Unidos`);
  }
  return clean({ country, state, city, address });
}

function clean(v: AddressValue): AddressValue {
  return {
    country: v.country,
    ...(v.state ? { state: v.state } : {}),
    ...(v.city ? { city: v.city } : {}),
    ...(v.address ? { address: v.address } : {}),
  };
}

/** Valor a guardar (JSON string normalizado). */
export function serializeAddress(value: AddressValue): string {
  return JSON.stringify(clean(value));
}

/** Lee sin lanzar (valores ya guardados): null si no sirve. */
export function readAddress(raw: unknown): AddressValue | null {
  try {
    return parseAddress(raw);
  } catch {
    return null;
  }
}

export function countryLabel(code: string): string {
  return COUNTRIES.find((c) => c.value === code)?.label ?? code;
}

export function stateLabel(country: string, state?: string): string {
  if (!state) return "";
  if (country === "HN") return HONDURAS_DEPARTMENTS.find((d) => d.value === state)?.label ?? state;
  if (country === "US") return US_STATES.find((s) => s.value === state)?.label ?? state;
  return state;
}

/**
 * Texto de una línea: «Col. Palmira, Distrito Central, Francisco Morazán,
 * Honduras». `withCountry: false` omite el país.
 */
export function formatAddress(value: AddressValue, { withCountry = true } = {}): string {
  return [value.address, value.city, stateLabel(value.country, value.state), withCountry ? countryLabel(value.country) : ""]
    .filter((p) => p && p.trim())
    .join(", ");
}

/** Partes con nombre (PDF, reportes, scripts). */
export function addressParts(value: AddressValue) {
  return {
    country: value.country,
    countryName: countryLabel(value.country),
    state: value.state ?? "",
    stateName: stateLabel(value.country, value.state),
    city: value.city ?? "",
    address: value.address ?? "",
    text: formatAddress(value),
  };
}
